/**
 * Create App Manifest page.
 *
 * Route: /admin/app-catalogue/new
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft } from '@tabler/icons-react';
import { Stack, Group, Title, Text, Button, toast } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { useApplicationCatalogueService } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';
import { AppManifestForm } from './AppManifestForm';

export default function NewAppPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (manifest: AppManifest) => {
    setSaving(true);
    try {
      await catalogueService.createApp(manifest);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'app-catalogue', 'all'] });
      toast.success(`${manifest.displayName} created`);
      navigate(ADMIN_ROUTES.APP_CATALOGUE_APPS);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create app manifest');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Group gap="xs">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconArrowLeft size={14} />}
                onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_APPS)}
              >
                Back to catalogue
              </Button>
            </Group>
            <Title order={2} mt="xs">
              New App Manifest
            </Title>
            <Text c="dimmed" size="sm">
              Register a new app in the CCMS mobile constellation.
            </Text>
          </div>
        </Group>

        <AppManifestForm
          saving={saving}
          onSubmit={handleSubmit}
          onCancel={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_APPS)}
        />
      </Stack>
    </FadeIn>
  );
}
